/// <reference types="applepayjs" />
'use client';

import { API_ROUTES, useApiMutation } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { useCallback, useState } from 'react';
import type { ApplePayErrorResponse, ApplePayMerchantSession, ApplePayValidationRequest } from '../types';

type ApplePaySessionResponseBody<T = unknown> = {
  success: boolean;
  data?: T;
  error?: unknown;
};

const DEFAULT_SUPPORTED_NETWORKS = ['visa', 'masterCard'];
const LIBRARY_VERSION = 3;

function useApplePaySession(initialSession?: ApplePaySession) {
  const debug = createDebug({ isEnabled: false });
  const [session, setSession] = useState<ApplePaySession | undefined>(initialSession);

  const merchantValidationMutation = useApiMutation<ApplePayMerchantSession, ApplePayValidationRequest>(API_ROUTES.PAYMENTS.APPLE_PAY_VALIDATE_MERCHANT, 'POST');

  const getMerchantCapability = useCallback((): ApplePayJS.ApplePayMerchantCapability[] => {
    return ['supports3DS', 'supportsDebit'];
  }, []);

  const getSupportedNetworks = useCallback((): string[] => {
    return [...DEFAULT_SUPPORTED_NETWORKS];
  }, []);

  const createApplePaySession = useCallback((request: ApplePayJS.ApplePayPaymentRequest): ApplePaySessionResponseBody<ApplePaySession> => {
    if (typeof window === 'undefined' || typeof ApplePaySession === 'undefined') {
      return { success: false, error: 'ApplePaySession is not available in this environment' };
    }

    try {
      const newSession = new ApplePaySession(LIBRARY_VERSION, request);
      debug.log('Creating Apple Pay session with request:', request);
      setSession(newSession);
      return { success: true, data: newSession };
    } catch (error) {
      return { success: false, error };
    }
  }, []);

  const createApplePayPaymentRequest = useCallback(
    (
      countryCode: string,
      currencyCode: string,
      label: string,
      type: ApplePayJS.ApplePayLineItemType,
      amount: string
    ): ApplePaySessionResponseBody<ApplePayJS.ApplePayPaymentRequest> => {
      const request: ApplePayJS.ApplePayPaymentRequest = {
        countryCode,
        currencyCode,
        merchantCapabilities: getMerchantCapability(),
        supportedNetworks: getSupportedNetworks(),
        total: {
          label,
          type,
          amount,
        },
      };
      return { success: true, data: request };
    },
    [getMerchantCapability, getSupportedNetworks]
  );

  const getMerchantSession = useCallback(
    async (validationUrl: string): Promise<ApplePaySessionResponseBody<ApplePayMerchantSession>> => {
      try {
        const domain = typeof window !== 'undefined' && window.location?.hostname ? window.location.hostname : 'localhost.local';
        const result = await merchantValidationMutation.mutateAsync({ url: validationUrl, domain });
        return { success: true, data: result || undefined };
      } catch (error) {
        return { success: false, error: error as { responseData: ApplePayErrorResponse } };
      }
    },
    [merchantValidationMutation]
  );

  const completePaymentMethodSelection = useCallback(
    (amount: string, newTotalLabel: string, itemLabel: string): ApplePaySessionResponseBody => {
      if (!session) {
        return { success: false, error: 'ApplePaySession object is required.' };
      }

      const newTotal: ApplePayJS.ApplePayLineItem = { amount, label: newTotalLabel };
      const lineItems: ApplePayJS.ApplePayLineItem[] = [
        {
          amount,
          label: itemLabel,
        },
      ];

      try {
        (session as ApplePaySession).completePaymentMethodSelection?.(newTotal, lineItems);
        return { success: true };
      } catch (error) {
        return { success: false, error };
      }
    },
    [session]
  );

  const completeMerchantValidation = useCallback(
    async (merchantSession: unknown): Promise<ApplePaySessionResponseBody> => {
      if (!session) {
        return { success: false, error: 'ApplePaySession object is required.' };
      }

      try {
        debug.log('Calling completeMerchantValidation with:', merchantSession);
        (session as ApplePaySession).completeMerchantValidation?.(merchantSession);
        debug.log('completeMerchantValidation successful');
        return { success: true };
      } catch (error) {
        debug.forceError('completeMerchantValidation failed with error:', error);
        return { success: false, error };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session]
  );

  const completePayment = useCallback(
    async (data: { status: number }): Promise<ApplePaySessionResponseBody> => {
      if (!session) {
        return { success: false, error: 'ApplePaySession object is required.' };
      }

      try {
        (session as ApplePaySession).completePayment?.(data);
        return { success: true };
      } catch (error) {
        return { success: false, error };
      }
    },
    [session]
  );

  const invalidateSession = useCallback(() => {
    setSession(undefined);
  }, []);

  return {
    session,
    createApplePaySession,
    createApplePayPaymentRequest,
    getMerchantSession,
    completePaymentMethodSelection,
    completeMerchantValidation,
    completePayment,
    invalidateSession,
    isMerchantValidationLoading: merchantValidationMutation.isPending,
    merchantValidationError: merchantValidationMutation.error,
  };
}

export { useApplePaySession };
